import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Droplets, Gift, ArrowRight, Sparkles, Users, MessageCircle } from 'lucide-react';

export default function Welcome() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [showContent, setShowContent] = useState(false);

  const referrerName = location.state?.referrerName || null;
  const userName = location.state?.userName || user?.name?.split(' ')[0] || 'Usuario';

  useEffect(() => {
    // Animate content after mount
    const timer = setTimeout(() => setShowContent(true), 100);
    return () => clearTimeout(timer);
  }, []); // Empty deps is correct - only run on mount

  const handleContinue = () => {
    navigate('/dashboard');
  };

  const appUrl = window.location.origin;
  const referralLink = `${appUrl}/ref/${user?.referral_code}`;
  const whatsappMessage = `Hola. Yo ya disfruto del agua de Manklar. Descarga la app y utiliza mi código ${user?.referral_code} para obtener tus beneficios.\n\n${referralLink}`;
  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(whatsappMessage)}`;

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: '#050505' }}>
      <div className={`w-full max-w-lg text-center transition-all duration-700 ${showContent ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        
        {/* Celebration Animation */}
        <div className="relative mb-8">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-40 h-40 rounded-full bg-white/5 animate-ping" style={{ animationDuration: '2s' }} />
          </div>
          <div className="relative w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] border-2 border-white/20 flex items-center justify-center shadow-[0_0_50px_rgba(255,255,255,0.2)]">
            <Droplets className="w-12 h-12 text-white" />
            <Sparkles className="absolute -top-2 -right-2 w-8 h-8 text-white/70 animate-pulse" />
          </div>
        </div>

        {/* Welcome Message */}
        <h1 className="text-4xl sm:text-5xl font-light text-white font-['Outfit'] mb-4">
          ¡Bienvenido a Manklar!
        </h1>
        
        <p className="text-xl text-white mb-2">
          Hola, {userName} 👋
        </p>

        {/* Referrer Info */}
        {referrerName && (
          <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-[#25D366]/10 border border-[#25D366]/30 mb-8 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
            <Gift className="w-5 h-5 text-[#25D366]" />
            <p className="text-[#25D366]">
              Has sido invitado por <span className="font-semibold">{referrerName}</span>
            </p>
          </div>
        )}

        {!referrerName && (
          <p className="text-[#A1A1AA] mb-8">
            Tu cuenta ha sido creada correctamente
          </p>
        )}

        {/* Benefits Card */}
        <div className="p-6 rounded-2xl bg-gradient-to-b from-[#0f0f0f] to-[#0a0a0a] border border-white/10 mb-8 text-left animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
          <h3 className="text-lg font-medium text-white mb-4 font-['Outfit']">
            🎁 ¿Qué puedes hacer ahora?
          </h3>
          
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                <Users className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-white font-medium">Invita a amigos y familia</p>
                <p className="text-sm text-[#A1A1AA]">Cada referido validado = 1 mes gratis</p>
              </div>
            </div>
            
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                <Gift className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-white font-medium">Consigue 10 referidos</p>
                <p className="text-sm text-[#A1A1AA]">Y gana 6 meses completamente GRATIS</p>
              </div>
            </div>
          </div>
        </div>

        {/* Your Code */}
        {user?.referral_code && (
          <div className="p-4 rounded-xl bg-[#0A0A0A] border border-white/10 mb-6 animate-fade-in-up" style={{ animationDelay: '0.5s' }}>
            <p className="text-xs tracking-wider uppercase text-[#52525B] mb-2">Tu código personal</p>
            <p className="text-3xl font-mono text-white tracking-widest">{user.referral_code}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="space-y-3 animate-fade-in-up" style={{ animationDelay: '0.6s' }}>
          <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="block">
            <Button 
              className="w-full h-14 bg-[#25D366] text-black hover:bg-[#20bd5a] rounded-xl font-bold text-base transition-all shadow-[0_0_30px_rgba(37,211,102,0.3)] hover:shadow-[0_0_40px_rgba(37,211,102,0.5)] flex items-center justify-center gap-3"
              data-testid="welcome-whatsapp-btn"
            >
              <MessageCircle className="w-6 h-6" />
              Compartir por WhatsApp
            </Button>
          </a>
          
          <Button
            onClick={handleContinue}
            className="w-full h-14 bg-white text-black hover:bg-neutral-100 rounded-xl font-semibold text-base transition-all"
            data-testid="welcome-continue-btn"
          >
            Ir a mi panel
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>

        {/* Skip link */}
        <button
          onClick={handleContinue}
          className="mt-6 text-sm text-[#52525B] hover:text-[#A1A1AA] transition-colors"
        >
          Continuar más tarde →
        </button>
      </div>
    </div>
  );
}
